import React, { useState, useEffect, useContext } from "react";
import { ChevronDown, RefreshCw, Play, ExternalLink } from "lucide-react";
import { AuthContext } from "../../../context/AuthContext";
import PetHealthReportPDF from "./PetHealthReportPDF";

const SelfCheckUp = () => {
  function calculateAge(dob) {
    const birthDate = new Date(dob);
    const today = new Date();

    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    let days = today.getDate() - birthDate.getDate();

    // Fix negative days
    if (days < 0) {
      months--;
      const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += prevMonth.getDate();
    }

    // Fix negative months
    if (months < 0) {
      years--;
      months += 12;
    }

    if (years > 0) {
      return `${years} year(s) ${months} month(s)`;
    } else if (months > 0) {
      return `${months} month(s) ${days} day(s)`;
    } else {
      return `${days} day(s)`;
    }
  }

  const { getPets, pets } = useContext(AuthContext);

  // Pet selector states
  const [selectedPetId, setSelectedPetId] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);

  const [formData, setFormData] = useState({
    // Pet Basic Info
    petName: "",
    petType: "",
    breed: "",
    sex: "",
    age: "",
    dateOfBirth: "",
    weight: "",

    // General Health
    appetite: "",
    energyLevel: "",
    sleepPattern: "",
    overallBehavior: "",

    // Physical Symptoms
    gumColor: "",
    eyeCondition: "",
    earCondition: "",
    noseCondition: "",

    // Digestive Health
    bowelMovements: "",
    urination: "",

    // Movement & Mobility
    walking: "",

    // Skin & Coat
    skinCondition: "",

    // Additional Concerns
    additionalSymptoms: "",
    medications: "",
    recentChanges: "",
    emergencyContact: "",
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [results, setResults] = useState(null);
  const [geminiAnalysis, setGeminiAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState("");

  // Your Gemini API key
  const API_KEY = "AIzaSyBm8i6msjWnHFdCxWFHM69hiG-KLJd5Reo";
  // Use 'gemini-1.5-flash-001' instead of just 'gemini-1.5-flash'
  // Updated to use Gemini 2.0 Flash which is available in your account
const GEMINI_API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${API_KEY}`;
  // Fetch pets when component mounts
  useEffect(() => {
    if (pets?.length === 0) {
      getPets();
    }
  }, [getPets, pets]);

  // Auto-populate form when pet is selected
  useEffect(() => {
    if (selectedPetId) {
      const selectedPet = pets.find((pet) => pet._id === selectedPetId);
      if (selectedPet) {
        setFormData((prev) => ({
          ...prev,
          petName: selectedPet.name,
          petType: selectedPet.species.toLowerCase(),
          breed: selectedPet.breed,
          dateOfBirth: selectedPet.dateOfBirth
            ? new Date(selectedPet.dateOfBirth).toISOString().split("T")[0]
            : "",
          age: selectedPet.age,
          sex: selectedPet.gender,
          weight: selectedPet.weight.toString(),
        }));
      }
    }
  }, [selectedPetId, pets]);

  const getAgeCategory = (age) => {
    if (age <= 1) return "puppy_kitten";
    if (age <= 3) return "young";
    if (age <= 7) return "adult";
    return "senior";
  };

  const getPetEmoji = (species) => {
    switch (species?.toLowerCase()) {
      case "dog":
        return "🐕";
      case "cat":
        return "🐱";
      case "bird":
        return "🦜";
      case "rabbit":
        return "🐰";
      case "hamster":
        return "🐹";
      case "fish":
        return "🐠";
      default:
        return "🐾";
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePetSelect = (pet) => {
    setSelectedPetId(pet._id);
    setDropdownOpen(false);
  };

  const clearPetSelection = () => {
    setSelectedPetId("");
    setFormData({
      petName: "",
      petType: "",
      breed: "",
      dateOfBirth: "",
      age: "",
      sex: "",
      weight: "",
      appetite: "",
      energyLevel: "",
      sleepPattern: "",
      overallBehavior: "",
      gumColor: "",
      eyeCondition: "",
      earCondition: "",
      noseCondition: "",
      bowelMovements: "",
      urination: "",
      walking: "",
      skinCondition: "",
      additionalSymptoms: "",
      medications: "",
      recentChanges: "",
      emergencyContact: "",
    });
  };

  // Function to create a structured prompt for Gemini
  const createGeminiPrompt = (petData) => {
    const prompt = `
You are a veterinary AI assistant analyzing a pet health check-up. Please analyze the following pet health data and provide a comprehensive assessment:

**Pet Information:**
- Name: ${petData.petName}
- Type: ${petData.petType}
- Breed: ${petData.breed}
- Age: ${calculateAge(petData.dateOfBirth)}

- sex: ${petData.gender}
- Weight: ${petData.weight} kg

**Health Observations:**
- Appetite: ${petData.appetite}
- Energy Level: ${petData.energyLevel}
- Sleep Pattern: ${petData.sleepPattern}
- Overall Behavior: ${petData.overallBehavior}

**Physical Examination:**
- Gum Color: ${petData.gumColor}
- Eyes Condition: ${petData.eyeCondition}
- Ears Condition: ${petData.earCondition}
- Nose Condition: ${petData.noseCondition}

**Digestive & Movement:**
- Bowel Movements: ${petData.bowelMovements}
- Urination: ${petData.urination}
- Walking/Movement: ${petData.walking}
- Skin & Coat: ${petData.skinCondition}

**Additional Information:**
- Current Medications: ${petData.medications}
- Additional Symptoms: ${petData.additionalSymptoms}
- Recent Changes: ${petData.recentChanges}

Please provide your analysis in the following JSON format:
{
  "riskLevel": "low|medium|high",
  "overallAssessment": "Brief overall health summary",
  "keyFindings": ["finding1", "finding2", "finding3"],
  "recommendations": {
    "homecure": ["home remedy 1", "home remedy 2", "home remedy 3"],
    "immediate": ["immediate action 1", "immediate action 2"],
    "shortTerm": ["short term care 1", "short term care 2"],
    "longTerm": ["long term care 1", "long term care 2"]
  },
  "warningSignsToWatch": ["warning sign 1", "warning sign 2"],
  "veterinaryVisit": "immediate|within_days|routine|not_needed",
  "confidenceLevel": "high|medium|low",
  "additionalNotes": "Any additional veterinary insights",
  "youtubeReferenceLinks": [
    {
      "title": "video title 1",
      "url": "youtube link 1",
      "language": "Hindi",
      "description": "Basic pet care tips in Hindi"
    },
    {
      "title": "video title 2",
      "url": "youtube link 2",
      "language": "English",
      "description": "Common pet health issues and solutions"
    },
    {
      "title": "video title 3",
      "url": "youtube link 3",
      "language": "Hindi",
      "description": "Home remedies for pet health problems"
    },
    {
      "title": "video title 4",
      "url": "youtube link 4",
      "language": "Marathi",
      "description": "Pet care guidance in Marathi"
    },
    {
      "title": "video title 5",
      "url": "youtube link 15",
      "language": "Hindi",
      "description": "Common diseases in dogs and cats"
    }
  ]
}
refer youtube to get upto 5 referal link of youtube video if not available don't get any link in youtubeReferenceLinks
Important: Base your assessment only on the provided information. Always recommend professional veterinary consultation for any concerning symptoms. For homecure recommendations, only suggest safe, natural remedies that are commonly known to be safe for pets.`;

    return prompt;
  };

  // Function to call Gemini API
  const analyzeWithGemini = async (petData) => {
    setIsAnalyzing(true);
    setError("");

    //console.log("pet Data: ", petData);
    console.log("pet Age is : ", calculateAge(petData.dateOfBirth));

    try {
      const prompt = createGeminiPrompt(petData);

      const response = await fetch(GEMINI_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contents: [
            {
              parts: [
                {
                  text: prompt,
                },
              ],
            },
          ],
          generationConfig: {
            temperature: 0.4,
            topK: 32,
            topP: 1,
            maxOutputTokens: 2048,
          },
        }),
      });
      console.log(response);
      if (!response.ok) {
        throw new Error(`Gemini API error: ${response.status}`);
      }

      const data = await response.json();
      const analysisText = data.candidates[0].content.parts[0].text;

      // Try to parse JSON from the response
      try {
        const jsonMatch = analysisText.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const analysis = JSON.parse(jsonMatch[0]);
          setGeminiAnalysis(analysis);
        } else {
          // If no JSON found, create a basic structure
          setGeminiAnalysis({
            riskLevel: "medium",
            overallAssessment: analysisText,
            keyFindings: ["Analysis completed"],
            recommendations: {
              homecure: [
                "Monitor pet closely",
                "Ensure proper hydration",
                "Provide comfortable resting area",
              ],
              immediate: ["Consult with veterinarian"],
              shortTerm: ["Monitor pet closely"],
              longTerm: ["Regular check-ups"],
            },
            warningSignsToWatch: ["Any changes in behavior"],
            veterinaryVisit: "within_days",
            confidenceLevel: "medium",
            additionalNotes: "AI analysis based on provided symptoms",
            youtubeReferenceLinks: [],
          });
        }
      } catch (parseError) {
        console.error("Error parsing Gemini response:", parseError);
        setError("Error parsing AI analysis. Please try again.");
      }
    } catch (err) {
      console.error("Gemini API Error:", err);
      setError(
        "Failed to analyze with AI. Please check your API key and try again."
      );
    } finally {
      setIsAnalyzing(false);
    }
  };

  // YouTube Video Card Component
  const YouTubeVideoCard = ({ video }) => (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
            <Play className="w-6 h-6 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h5 className="font-medium text-gray-900 text-sm line-clamp-2 mb-1">
              {video.title}
            </h5>
            <p className="text-xs text-gray-500 mb-2">{video.description}</p>
            <div className="flex items-center justify-between">
              <span
                className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                  video.language === "Hindi"
                    ? "bg-orange-100 text-orange-800"
                    : video.language === "English"
                    ? "bg-blue-100 text-blue-800"
                    : video.language === "Marathi"
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {video.language}
              </span>
              <a
                href={video.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <span>Watch</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResults(formData);
    await analyzeWithGemini(formData);
  };

  const nextStep = () => setCurrentStep((prev) => prev + 1);
  const prevStep = () => setCurrentStep((prev) => prev - 1);

  const renderStepIndicator = () => (
    <div className="flex justify-center mb-6 sm:mb-8 px-2 sm:px-2">
      <div className="flex items-center overflow-x-auto max-w-full">
        {[0, 1, 2].map((step) => (
          <div key={step} className="flex items-center flex-shrink-0">
            <div
              className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium mb-2 ${
                currentStep >= step
                  ? "bg-blue-600 text-white"
                  : "bg-gray-300 text-gray-600"
              }`}
            >
              {step + 1}
            </div>
            {step < 2 && (
              <div
                className={`w-8 sm:w-12 h-0.5 sm:h-1 mx-1 sm:mx-0 ${
                  currentStep > step ? "bg-blue-600" : "bg-gray-300"
                }`}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Pet Selector Step
  const renderPetSelector = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">
        Select Your Pet
      </h3>

      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
        <p className="text-blue-800 text-sm">
          💡 <strong>Quick Start:</strong> Select one of your registered pets to
          auto-fill basic information, or skip to manually enter details.
        </p>
      </div>

      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Choose from your registered pets:
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between"
          >
            <div className="flex items-center space-x-3">
              {selectedPetId ? (
                (() => {
                  const selectedPet = pets.find(
                    (pet) => pet._id === selectedPetId
                  );
                  return selectedPet ? (
                    <>
                      <span className="text-2xl">
                        {getPetEmoji(selectedPet.species)}
                      </span>
                      <div>
                        <span className="font-medium text-gray-900">
                          {selectedPet.name}
                        </span>
                        <span className="text-sm text-gray-500 ml-2">
                          ({selectedPet.species})
                        </span>
                      </div>
                    </>
                  ) : (
                    <span className="text-gray-500">Choose a pet...</span>
                  );
                })()
              ) : (
                <span className="text-gray-500">Choose a pet...</span>
              )}
            </div>
            <ChevronDown
              className={`w-5 h-5 text-gray-400 transition-transform ${
                dropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {dropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
              <div className="py-1">
                {pets?.map((pet) => (
                  <button
                    key={pet._id}
                    type="button"
                    onClick={() => handlePetSelect(pet)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors"
                  >
                    <span className="text-2xl">{getPetEmoji(pet.species)}</span>
                    <div>
                      <div className="font-medium text-gray-900">
                        {pet.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {pet.breed} • {pet.species} •{" "}
                        {(() => {
                          if (!pet.dateOfBirth) return "Unknown age";

                          const today = new Date();
                          const birthDate = new Date(pet.dateOfBirth);

                          // Difference in ms → days
                          const diffMs = today - birthDate;
                          const diffDays = Math.floor(
                            diffMs / (1000 * 60 * 60 * 24)
                          );
                          const diffMonths =
                            today.getMonth() -
                            birthDate.getMonth() +
                            12 *
                              (today.getFullYear() - birthDate.getFullYear());
                          const diffYears =
                            today.getFullYear() - birthDate.getFullYear();

                          // Adjust years correctly
                          let ageYears = diffYears;
                          if (
                            today.getMonth() < birthDate.getMonth() ||
                            (today.getMonth() === birthDate.getMonth() &&
                              today.getDate() < birthDate.getDate())
                          ) {
                            ageYears--;
                          }

                          if (ageYears >= 1) {
                            return `${ageYears} year${ageYears > 1 ? "s" : ""}`;
                          } else if (diffMonths >= 1) {
                            return `${diffMonths} month${
                              diffMonths > 1 ? "s" : ""
                            }`;
                          } else {
                            return `${diffDays} day${
                              diffDays !== 1 ? "s" : ""
                            }`;
                          }
                        })()}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {selectedPetId && (
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={clearPetSelection}
              className="text-sm text-gray-600 hover:text-gray-800 flex items-center space-x-1"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Clear selection and enter manually</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">
          Pet Information
        </h3>
        {selectedPetId && (
          <button
            type="button"
            onClick={() => {
              clearPetSelection();
              setCurrentStep(0);
            }}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            ← Change Pet
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pet Name *
          </label>
          <input
            type="text"
            name="petName"
            value={formData.petName}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-200"
            required
            readOnly
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pet Type *
          </label>
          <select
            name="petType"
            value={formData.petType}
            onChange={handleInputChange}
            className="bg-gray-200 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
            readOnly
          >
            <option value="">Select pet type</option>
            <option value="dog">Dog</option>
            <option value="cat">Cat</option>
            <option value="bird">Bird</option>
            <option value="rabbit">Rabbit</option>
            <option value="hamster">Hamster</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Breed
          </label>
          <input
            type="text"
            name="breed"
            value={formData.breed}
            onChange={handleInputChange}
            readOnly
            className="bg-gray-200 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date of Birth *
            {formData.dateOfBirth && (
              <span className="text-xs text-gray-500 ml-2">
                (
                {(() => {
                  const today = new Date();
                  const birthDate = new Date(formData.dateOfBirth);
                  {
                    console.log("Age is : ", birthDate);
                  }

                  // Difference in ms → days
                  const diffMs = today - birthDate;
                  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                  const diffMonths =
                    today.getMonth() -
                    birthDate.getMonth() +
                    12 * (today.getFullYear() - birthDate.getFullYear());
                  const diffYears =
                    today.getFullYear() - birthDate.getFullYear();

                  // Adjust years properly
                  let ageYears = diffYears;
                  if (
                    today.getMonth() < birthDate.getMonth() ||
                    (today.getMonth() === birthDate.getMonth() &&
                      today.getDate() < birthDate.getDate())
                  ) {
                    ageYears--;
                  }

                  if (ageYears >= 1) {
                    return `${ageYears} year${ageYears > 1 ? "s" : ""} old`;
                  } else if (diffMonths >= 1) {
                    return `${diffMonths} month${
                      diffMonths > 1 ? "s" : ""
                    } old`;
                  } else {
                    return `${diffDays} day${diffDays !== 1 ? "s" : ""} old`;
                  }
                })()}
                )
              </span>
            )}
          </label>
          <input
            type="date"
            name="dateOfBirth"
            value={formData.dateOfBirth}
            onChange={handleInputChange}
            readOnly
            className="bg-gray-200 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            max={new Date().toISOString().split("T")[0]}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sex *
          </label>
          <select
            name="sex"
            value={formData.sex}
            onChange={handleInputChange}
            className="bg-gray-200 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
            readOnly
          >
            <option value="">Select gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Weight (kg)
          </label>
          <input
            type="number"
            name="weight"
            value={formData.weight}
            onChange={handleInputChange}
            className="bg-gray-200 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            step="0.1"
            readOnly
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">
        Health Assessment
      </h3>

      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
        <p className="text-blue-800 text-sm">
          🩺 Please provide detailed information about your pet's health and
          symptoms.
        </p>
      </div>

      <div className="space-y-8">
        {/* General Health */}
        <div>
          <h4 className="text-lg font-medium text-gray-700 mb-4">
            General Health & Behavior
          </h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Appetite
              </label>
              <select
                name="appetite"
                value={formData.appetite}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select appetite level</option>
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="decreased">Decreased</option>
                <option value="loss_of_appetite">Loss of appetite</option>
                <option value="increased">Increased appetite</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Energy Level
              </label>
              <select
                name="energyLevel"
                value={formData.energyLevel}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select energy level</option>
                <option value="very_active">Very Active</option>
                <option value="normal">Normal</option>
                <option value="less_active">Less Active</option>
                <option value="lethargy">Lethargic</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sleep Pattern
              </label>
              <select
                name="sleepPattern"
                value={formData.sleepPattern}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select sleep pattern</option>
                <option value="normal">Normal</option>
                <option value="more_sleeping">Sleeping more than usual</option>
                <option value="restless">Restless/Less sleep</option>
                <option value="difficulty_sleeping">Difficulty sleeping</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Overall Behavior
              </label>
              <select
                name="overallBehavior"
                value={formData.overallBehavior}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select behavior</option>
                <option value="normal">Normal</option>
                <option value="more_affectionate">More affectionate</option>
                <option value="withdrawn">Withdrawn/Hiding</option>
                <option value="aggressive">More aggressive</option>
                <option value="anxious">Anxious/Stressed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Physical Examination */}
        <div>
          <h4 className="text-lg font-medium text-gray-700 mb-4">
            Physical Examination
          </h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gum Color
              </label>
              <select
                name="gumColor"
                value={formData.gumColor}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select gum color</option>
                <option value="pink">Pink (Normal)</option>
                <option value="pale">Pale</option>
                <option value="white">White</option>
                <option value="yellow">Yellow</option>
                <option value="red">Red/Inflamed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Eyes Condition
              </label>
              <select
                name="eyeCondition"
                value={formData.eyeCondition}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select eye condition</option>
                <option value="clear">Clear and bright</option>
                <option value="watery">Watery</option>
                <option value="discharge">Discharge</option>
                <option value="red">Red/Inflamed</option>
                <option value="cloudy">Cloudy</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ears Condition
              </label>
              <select
                name="earCondition"
                value={formData.earCondition}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select ear condition</option>
                <option value="clean">Clean</option>
                <option value="waxy">Waxy buildup</option>
                <option value="discharge">Discharge</option>
                <option value="odor">Bad odor</option>
                <option value="scratching">Excessive scratching</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nose Condition
              </label>
              <select
                name="noseCondition"
                value={formData.noseCondition}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select nose condition</option>
                <option value="moist">Moist (Normal)</option>
                <option value="dry">Dry</option>
                <option value="discharge">Discharge</option>
                <option value="cracked">Cracked</option>
                <option value="warm">Warm to touch</option>
              </select>
            </div>
          </div>
        </div>

        {/* Digestive & Movement Health */}
        <div>
          <h4 className="text-lg font-medium text-gray-700 mb-4">
            Digestive & Movement Health
          </h4>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bowel Movements
              </label>
              <select
                name="bowelMovements"
                value={formData.bowelMovements}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select bowel movement condition</option>
                <option value="normal">Normal</option>
                <option value="loose">Loose stool</option>
                <option value="diarrhea">Diarrhea</option>
                <option value="constipated">Constipated</option>
                <option value="blood">Blood in stool</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Urination
              </label>
              <select
                name="urination"
                value={formData.urination}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select urination pattern</option>
                <option value="normal">Normal</option>
                <option value="frequent">More frequent</option>
                <option value="straining">Straining to urinate</option>
                <option value="accidents">Having accidents</option>
                <option value="blood">Blood in urine</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Walking/Movement
              </label>
              <select
                name="walking"
                value={formData.walking}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select movement condition</option>
                <option value="normal">Normal</option>
                <option value="stiff">Stiff movement</option>
                <option value="limping">Limping</option>
                <option value="reluctant">Reluctant to move</option>
                <option value="difficulty">Difficulty standing</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Skin & Coat
              </label>
              <select
                name="skinCondition"
                value={formData.skinCondition}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select skin condition</option>
                <option value="healthy">Healthy</option>
                <option value="dry">Dry skin</option>
                <option value="itchy">Itchy/Scratching</option>
                <option value="rash">Rash or bumps</option>
                <option value="hair_loss">Hair loss</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">
        Additional Information
      </h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Medications
          </label>
          <textarea
            name="medications"
            value={formData.medications}
            onChange={handleInputChange}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="List any medications, supplements, or treatments your pet is currently taking..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Symptoms or Concerns
          </label>
          <textarea
            name="additionalSymptoms"
            value={formData.additionalSymptoms}
            onChange={handleInputChange}
            rows="4"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Describe any other symptoms, behaviors, or concerns you've noticed..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Recent Changes
          </label>
          <textarea
            name="recentChanges"
            value={formData.recentChanges}
            onChange={handleInputChange}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Any recent changes in diet, environment, routine, or other factors..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Emergency Contact
          </label>
          <input
            type="text"
            name="emergencyContact"
            value={formData.emergencyContact}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Your veterinarian's contact information"
          />
        </div>
      </div>
    </div>
  );

  const renderResults = () => (
    <div className="space-y-6">
      {/* Loading State */}
      {isAnalyzing && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">
            🤖 Analyzing your pet's health with AI...
          </p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Analysis Error
              </h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Results - Next.js Component with Tailwind CSS */}
      {geminiAnalysis && !isAnalyzing && (
        <>
          {/* Print-specific styles */}
          <style jsx>{`
            @media print {
              @page {
                size: A4;
                margin: 0.5in;
              }

              .print-container {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                height: auto !important;
                max-height: none !important;
                font-size: 11px !important;
                line-height: 1.2 !important;
              }

              .print-section {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                margin-bottom: 8px !important;
              }

              .print-grid {
                display: grid !important;
                grid-template-columns: 1fr 1fr 1fr 1fr !important;
                gap: 6px !important;
              }

              .print-compact {
                padding: 6px !important;
                margin-bottom: 4px !important;
              }

              .print-text-xs {
                font-size: 10px !important;
                line-height: 1.1 !important;
              }

              .print-text-sm {
                font-size: 11px !important;
                line-height: 1.2 !important;
              }

              .print-hide {
                display: none !important;
              }
            }
          `}</style>

          <div className="max-w-4xl mx-auto bg-white border-2 border-gray-800 rounded-lg overflow-hidden shadow-lg print:shadow-none print:border-black print-container print:max-w-none print:mx-0 print:border print:rounded-none">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white text-center py-3 print-section print-compact">
              <h1 className="text-xl font-bold print:text-lg">
                VetSense AI Health Analysis
              </h1>
            </div>

            {/* Priority & Confidence Section */}
            <div className="flex justify-between items-center p-3 bg-gray-50 border-b print-section print-compact">
              <div>
                <span
                  className={`inline-block px-3 py-1 rounded-full text-white font-semibold text-sm print-text-xs ${
                    geminiAnalysis.riskLevel === "high"
                      ? "bg-red-500"
                      : geminiAnalysis.riskLevel === "medium"
                      ? "bg-yellow-500"
                      : "bg-green-500"
                  }`}
                >
                  {geminiAnalysis.riskLevel === "high"
                    ? "⚠️ High Priority"
                    : geminiAnalysis.riskLevel === "medium"
                    ? "⚡ Medium Priority"
                    : "✅ Low Priority"}
                </span>
              </div>
              <div>
                <span
                  className={`text-xs px-2 py-1 rounded-full print-text-xs ${
                    geminiAnalysis.confidenceLevel === "high"
                      ? "bg-green-100 text-green-800"
                      : geminiAnalysis.confidenceLevel === "medium"
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  Confidence level: {geminiAnalysis.confidenceLevel}
                </span>
              </div>
            </div>

            {/* Pet Information Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 p-3 border-b print-section print-compact">
              <div className="lg:col-span-2">
                <div className="grid grid-cols-3 gap-2 text-xs print-text-xs">
                  <div>
                    <strong className="block text-gray-700">Name:</strong>
                    <span className="text-gray-600">
                      {formData.petName || "N/A"}
                    </span>
                  </div>
                  <div>
                    <strong className="block text-gray-700">Age:</strong>
                    <span className="text-gray-600">
                      {formData.dateOfBirth
                        ? (() => {
                            const today = new Date();
                            const birthDate = new Date(formData.dateOfBirth);

                            // Difference in ms → days
                            const diffMs = today - birthDate;
                            const diffDays = Math.floor(
                              diffMs / (1000 * 60 * 60 * 24)
                            );
                            const diffMonths =
                              today.getMonth() -
                              birthDate.getMonth() +
                              12 *
                                (today.getFullYear() - birthDate.getFullYear());
                            const diffYears =
                              today.getFullYear() - birthDate.getFullYear();

                            // Adjust years correctly
                            let ageYears = diffYears;
                            if (
                              today.getMonth() < birthDate.getMonth() ||
                              (today.getMonth() === birthDate.getMonth() &&
                                today.getDate() < birthDate.getDate())
                            ) {
                              ageYears--;
                            }

                            if (ageYears >= 1) {
                              return `${ageYears} year${
                                ageYears > 1 ? "s" : ""
                              } old`;
                            } else if (diffMonths >= 1) {
                              return `${diffMonths} month${
                                diffMonths > 1 ? "s" : ""
                              } old`;
                            } else {
                              return `${diffDays} day${
                                diffDays !== 1 ? "s" : ""
                              } old`;
                            }
                          })()
                        : "N/A"}
                    </span>
                  </div>
                  <div>
                    <strong className="block text-gray-700">Sex:</strong>
                    <span className="text-gray-600">
                      {formData.sex || "N/A"}
                    </span>
                  </div>
                  <div>
                    <strong className="block text-gray-700">Species:</strong>
                    <span className="text-gray-600 capitalize">
                      {formData.petType || "N/A"}
                    </span>
                  </div>
                  <div>
                    <strong className="block text-gray-700">Breed:</strong>
                    <span className="text-gray-600">
                      {formData.breed || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary of Symptoms */}
            <div className="p-2 border-b print-section print-compact">
              <h3 className="text-xs font-semibold text-gray-700 mb-1 print-text-xs">
                📋 Summary of symptoms
              </h3>
              <p className="text-xs text-gray-600 print-text-xs">
                {formData.additionalSymptoms ||
                  "No specific symptoms reported. General health assessment completed."}
              </p>
            </div>

            {/* Main Content Grid */}
            <div className="p-2 print-section print-compact">
              {/* Overall Assessment */}
              <div className="mb-3">
                <h3 className="font-semibold mb-1 text-sm print-text-sm">
                  Overall Assessment:
                </h3>
                <div
                  className={`p-2 rounded border-l-4 text-xs print-text-xs ${
                    geminiAnalysis.riskLevel === "high"
                      ? "bg-red-50 border-red-400"
                      : geminiAnalysis.riskLevel === "medium"
                      ? "bg-yellow-50 border-yellow-400"
                      : "bg-green-50 border-green-400"
                  }`}
                >
                  <p className="text-gray-700">
                    {geminiAnalysis.overallAssessment}
                  </p>
                </div>
              </div>

              {/* Key Findings */}
              {geminiAnalysis.keyFindings &&
                geminiAnalysis.keyFindings.length > 0 && (
                  <div className="bg-blue-50 p-2 rounded border border-blue-200 mb-3 print-compact">
                    <h4 className="font-semibold text-blue-800 mb-1 text-sm print-text-sm">
                      🔍 Key Findings:
                    </h4>
                    <ul className="text-xs space-y-1 print-text-xs">
                      {geminiAnalysis.keyFindings.map((finding, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-blue-600 mr-1">•</span>
                          <span className="text-blue-700">{finding}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              {/* Recommendations Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3 print-grid">
                {/* Home Care Solutions */}
                {geminiAnalysis.recommendations?.homecure &&
                  geminiAnalysis.recommendations.homecure.length > 0 && (
                    <div className="bg-green-50 p-2 rounded border border-green-200 print-compact">
                      <h5 className="font-semibold text-green-800 mb-1 text-xs print-text-xs">
                        🏠 Home Care:
                      </h5>
                      <ul className="space-y-1 text-xs print-text-xs">
                        {geminiAnalysis.recommendations.homecure
                          .slice(0, 3)
                          .map((rec, index) => (
                            <li key={index} className="text-green-700">
                              • {rec}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}

                {/* Immediate Actions */}
                {geminiAnalysis.recommendations?.immediate &&
                  geminiAnalysis.recommendations.immediate.length > 0 && (
                    <div className="bg-red-50 p-2 rounded border border-red-200 print-compact">
                      <h5 className="font-semibold text-red-800 mb-1 text-xs print-text-xs">
                        ⚡ Immediate:
                      </h5>
                      <ul className="space-y-1 text-xs print-text-xs">
                        {geminiAnalysis.recommendations.immediate
                          .slice(0, 3)
                          .map((rec, index) => (
                            <li key={index} className="text-red-700">
                              • {rec}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}

                {/* Short-term Care */}
                {geminiAnalysis.recommendations?.shortTerm &&
                  geminiAnalysis.recommendations.shortTerm.length > 0 && (
                    <div className="bg-yellow-50 p-2 rounded border border-yellow-200 print-compact">
                      <h5 className="font-semibold text-yellow-800 mb-1 text-xs print-text-xs">
                        📅 Short-term:
                      </h5>
                      <ul className="space-y-1 text-xs print-text-xs">
                        {geminiAnalysis.recommendations.shortTerm
                          .slice(0, 3)
                          .map((rec, index) => (
                            <li key={index} className="text-yellow-700">
                              • {rec}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}

                {/* Long-term Care */}
                {geminiAnalysis.recommendations?.longTerm &&
                  geminiAnalysis.recommendations.longTerm.length > 0 && (
                    <div className="bg-blue-50 p-2 rounded border border-blue-200 print-compact">
                      <h5 className="font-semibold text-blue-800 mb-1 text-xs print-text-xs">
                        🌱 Long-term:
                      </h5>
                      <ul className="space-y-1 text-xs print-text-xs">
                        {geminiAnalysis.recommendations.longTerm
                          .slice(0, 3)
                          .map((rec, index) => (
                            <li key={index} className="text-blue-700">
                              • {rec}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
              </div>
            </div>

            {/* Warning Signs & Vet Recommendation */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 p-2 border-t print-section print-compact">
              {/* Warning Signs */}
              {geminiAnalysis.warningSignsToWatch &&
                geminiAnalysis.warningSignsToWatch.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-1 text-sm print-text-sm">
                      ⚠️ Warning Signs:
                    </h4>
                    <div className="bg-orange-50 p-2 rounded border border-orange-200 print-compact">
                      <ul className="text-xs space-y-1 print-text-xs">
                        {geminiAnalysis.warningSignsToWatch
                          .slice(0, 4)
                          .map((warning, index) => (
                            <li key={index} className="flex items-start">
                              <span className="text-orange-600 mr-1">⚠️</span>
                              <span className="text-orange-700">{warning}</span>
                            </li>
                          ))}
                      </ul>
                    </div>
                  </div>
                )}

              {/* Veterinary Visit Recommendation */}
              <div>
                <h4 className="font-semibold mb-1 text-sm print-text-sm">
                  🏥 Vet Recommendation:
                </h4>
                <div
                  className={`p-2 rounded border print-compact ${
                    geminiAnalysis.veterinaryVisit === "immediate"
                      ? "bg-red-100 border-red-300"
                      : geminiAnalysis.veterinaryVisit === "within_days"
                      ? "bg-yellow-100 border-yellow-300"
                      : geminiAnalysis.veterinaryVisit === "routine"
                      ? "bg-blue-100 border-blue-300"
                      : "bg-green-100 border-green-300"
                  }`}
                >
                  <p
                    className={`text-xs font-medium print-text-xs ${
                      geminiAnalysis.veterinaryVisit === "immediate"
                        ? "text-red-800"
                        : geminiAnalysis.veterinaryVisit === "within_days"
                        ? "text-yellow-800"
                        : geminiAnalysis.veterinaryVisit === "routine"
                        ? "text-blue-800"
                        : "text-green-800"
                    }`}
                  >
                    {geminiAnalysis.veterinaryVisit === "immediate" &&
                      "🚨 Seek immediate veterinary attention"}
                    {geminiAnalysis.veterinaryVisit === "within_days" &&
                      "⏰ Schedule veterinary visit within days"}
                    {geminiAnalysis.veterinaryVisit === "routine" &&
                      "📅 Schedule routine check-up"}
                    {geminiAnalysis.veterinaryVisit === "not_needed" &&
                      "✅ Continue monitoring"}
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Notes */}
            {geminiAnalysis.additionalNotes && (
              <div className="p-2 border-t print-section print-compact">
                <h4 className="font-semibold mb-1 text-sm print-text-sm">
                  📝 Additional Notes:
                </h4>
                <div className="bg-gray-50 p-2 rounded border border-gray-200 print-compact">
                  <p className="text-xs text-gray-700 print-text-xs">
                    {geminiAnalysis.additionalNotes}
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-center gap-2 p-2 bg-gray-50 border-t print-hide">
              <button
                onClick={() => {
                  setResults(null);
                  setGeminiAnalysis(null);
                  setCurrentStep(0);
                  setError("");
                  setSelectedPetId("");
                  setFormData({
                    petName: "",
                    petType: "",
                    breed: "",
                    dateOfBirth: "",
                    age: "",
                    sex: "",
                    weight: "",
                    appetite: "",
                    energyLevel: "",
                    sleepPattern: "",
                    overallBehavior: "",
                    gumColor: "",
                    eyeCondition: "",
                    earCondition: "",
                    noseCondition: "",
                    bowelMovements: "",
                    urination: "",
                    walking: "",
                    skinCondition: "",
                    additionalSymptoms: "",
                    medications: "",
                    recentChanges: "",
                    emergencyContact: "",
                  });
                }}
                className="bg-blue-600 text-white px-3 py-2 rounded text-xs hover:bg-blue-700 transition-colors"
              >
                🔄 New Check-up
              </button>

              <button
                onClick={() => analyzeWithGemini(formData)}
                disabled={isAnalyzing}
                className="bg-purple-600 text-white px-3 py-2 rounded text-xs hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                🤖 Re-analyse with VetSense AI
              </button>

              <button
                onClick={() => window.print()}
                className="bg-gray-600 text-white px-3 py-2 rounded text-xs hover:bg-gray-700 transition-colors"
              >
                📄 Quick Print (Current View)
              </button>

              <button
                onClick={() => setShowPDFModal(true)}
                className="bg-green-600 text-white px-3 py-2 rounded text-xs hover:bg-green-700 transition-colors font-medium"
              >
                📄 Download Professional Report
              </button>

              <button
                onClick={() => {
                  // Add your booking logic here
                  alert(
                    `Book a Vet Appointment for ${
                      formData.petName || "your pet"
                    }`
                  );
                }}
                className="bg-orange-600 text-white px-3 py-2 rounded text-xs hover:bg-orange-700 transition-colors"
              >
                📅 Book Appointment
              </button>
            </div>
            {showPDFModal && (
              <PetHealthReportPDF
                formData={formData}
                geminiAnalysis={geminiAnalysis}
                onClose={() => setShowPDFModal(false)}
              />
            )}
          </div>
        </>
      )}
    </div>
  );

  if (results) {
    return (
      <div className="mx-auto py-6 bg-white min-h-screen">
        {renderResults()}
      </div>
    );
  }

  return (
    <div className="mx-auto py-6 bg-white min-h-screen">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          🤖 Vet Sence AI Enabled Animal Health checking
        </h1>
        <p className="text-gray-600">Advanced pet health analysis with AI</p>
      </div>

      {/* Step Indicator */}
      {renderStepIndicator()}

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-gray-50 rounded-lg p-6 border border-gray-200"
      >
        {currentStep === 0 && (
          <>
            {renderPetSelector()}
            {selectedPetId && renderStep1()}
          </>
        )}
        {currentStep === 1 && renderStep2()}
        {currentStep === 2 && renderStep3()}

        {/* Navigation Buttons */}
        {currentStep > 0 && (
          <div className="flex justify-between mt-8">
            <button
              type="button"
              onClick={prevStep}
              className="bg-gray-600 text-white py-2 px-6 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Previous
            </button>

            {currentStep < 3 ? (
              <button
                type="button"
                onClick={nextStep}
                className="bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                disabled={isAnalyzing}
                className="bg-green-600 text-white py-2 px-6 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {isAnalyzing ? "Analyzing..." : "🤖 Analyze with AI"}
              </button>
            )}
          </div>
        )}

        {currentStep === 0 && selectedPetId && (
          <div className="flex justify-end mt-8">
            <button
              type="button"
              onClick={nextStep}
              className="bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </form>

      {/* AI Disclaimer */}
      <div className="mt-6 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
        <p className="text-sm text-yellow-800">
          <strong>⚠️ AI Disclaimer:</strong> This AI analysis is for
          informational purposes only and does not replace professional
          veterinary care. The AI provides insights based on the symptoms you've
          described, but always consult with a qualified veterinarian for
          accurate diagnosis and treatment.
        </p>
      </div>

      {/* Features Info */}
      <div className="mt-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
        <h4 className="font-semibold text-blue-800 mb-2">
          🚀 AI-Powered Features:
        </h4>
        <div className="grid md:grid-cols-2 gap-2 text-sm text-blue-700">
          <div>
            <p>✅ Smart pet selection with auto-fill</p>
            <p>✅ Advanced symptom analysis</p>
            <p>✅ Risk level assessment</p>
            <p>✅ Home care solutions</p>
          </div>
          <div>
            <p>✅ Warning signs identification</p>
            <p>✅ Veterinary visit guidance</p>
            <p>✅ Confidence level scoring</p>
            <p>✅ Educational video references</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelfCheckUp;
